import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  LayoutGrid,
  AlertTriangle,
  Link2,
  GripVertical,
  User,
  FileDown,
  ClipboardList,
  Trash2,
  ChevronDown,
  X
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ActiviteHpDialog } from '../components/planning/ActiviteHpDialog';
import { PlanningExportPdfDialog } from '../components/planning/PlanningExportPdfDialog';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  addWeeks, 
  subWeeks, 
  eachDayOfInterval,
  parseISO,
  isWithinInterval,
  isToday,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  addDays,
  subDays
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { getAuthToken } from '../lib/authToken';
import { isJourFerieDate, getJourFerieLabel } from '../lib/joursFeries';
import { ACTIVITY_TYPES as SHARED_ACTIVITY_TYPES, ALL_ACTIVITY_TYPES, ACTIVITY_BY_VALUE } from '../lib/activites';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 14 distinct colors for formateurs
const FORMATEUR_COLORS = [
  { bg: 'bg-blue-600',    light: 'bg-blue-100',    text: 'text-blue-700',    border: 'border-blue-300',   hex: '#2563eb' },
  { bg: 'bg-emerald-600', light: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300', hex: '#059669' },
  { bg: 'bg-amber-500',   light: 'bg-amber-100',   text: 'text-amber-700',   border: 'border-amber-300',  hex: '#f59e0b' },
  { bg: 'bg-purple-600',  light: 'bg-purple-100',  text: 'text-purple-700',  border: 'border-purple-300', hex: '#9333ea' },
  { bg: 'bg-rose-500',    light: 'bg-rose-100',    text: 'text-rose-700',    border: 'border-rose-300',   hex: '#f43f5e' },
  { bg: 'bg-cyan-600',    light: 'bg-cyan-100',    text: 'text-cyan-700',    border: 'border-cyan-300',   hex: '#0891b2' },
  { bg: 'bg-orange-500',  light: 'bg-orange-100',  text: 'text-orange-700',  border: 'border-orange-300', hex: '#f97316' },
  { bg: 'bg-indigo-600',  light: 'bg-indigo-100',  text: 'text-indigo-700',  border: 'border-indigo-300', hex: '#4f46e5' },
  { bg: 'bg-teal-600',    light: 'bg-teal-100',    text: 'text-teal-700',    border: 'border-teal-300',   hex: '#0d9488' },
  { bg: 'bg-pink-500',    light: 'bg-pink-100',    text: 'text-pink-700',    border: 'border-pink-300',   hex: '#ec4899' },
  { bg: 'bg-lime-600',    light: 'bg-lime-100',    text: 'text-lime-700',    border: 'border-lime-300',   hex: '#65a30d' },
  { bg: 'bg-red-600',     light: 'bg-red-100',     text: 'text-red-700',     border: 'border-red-300',    hex: '#dc2626' },
  { bg: 'bg-sky-500',     light: 'bg-sky-100',     text: 'text-sky-700',     border: 'border-sky-300',    hex: '#0ea5e9' },
  { bg: 'bg-fuchsia-600', light: 'bg-fuchsia-100', text: 'text-fuchsia-700', border: 'border-fuchsia-300', hex: '#c026d3' },
];

export const PlanningPage = () => {
  const [dossiers, setDossiers] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [modules, setModules] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(() => new URLSearchParams(window.location.search).get('tab') === 'activites' ? 'activites' : 'gantt');
  const [loading, setLoading] = useState(true);
  const [draggedFormateur, setDraggedFormateur] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [financeursDispo, setFinanceursDispo] = useState([]);
  const [affectationsHP, setAffectationsHP] = useState([]);
  const [financeursParAf, setFinanceursParAf] = useState({}); // af_id -> "FINANCEUR A, FINANCEUR B" (regroupement du Gantt)
  const [dossiersFinanceurs, setDossiersFinanceurs] = useState([]);
  const [ganttFilters, setGanttFilters] = useState({ type: 'tous', financeur: 'all', entreprise: 'all', lieu: 'all' });
  const [draggedActivity, setDraggedActivity] = useState(null);
  const [draggedUnassigned, setDraggedUnassigned] = useState(null);
  const [draggedAssigned, setDraggedAssigned] = useState(null);
  const [removeHover, setRemoveHover] = useState(false);
  const [confirmDrop, setConfirmDrop] = useState(null);
  const [hpDialog, setHpDialog] = useState(null); // affectation HP (existante ou brouillon) ouverte dans le dialog
  const [hpSaving, setHpSaving] = useState(false);
  const navigate = useNavigate();

  const HP_LABELS = Object.fromEntries(ALL_ACTIVITY_TYPES.map(a => [a.value, a.label]));
  const ACTIVITY_TYPES = SHARED_ACTIVITY_TYPES;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dossiersRes, formateursRes, modulesRes, hpRes, dfRes] = await Promise.all([
        axios.get(`${API_URL}/dossiers`),
        axios.get(`${API_URL}/formateurs`),
        axios.get(`${API_URL}/modules`),
        axios.get(`${API_URL}/requetes/affectations-hors-production`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/dossiers-financeurs`).catch(() => ({ data: [] }))
      ]);
      setDossiers(dossiersRes.data);
      const nomsFin = new Set((dfRes.data || []).map(df => df.financeur_nom).filter(Boolean));
      dossiersRes.data.forEach(d => { if (d.financeur) nomsFin.add(d.financeur); });
      setFinanceursDispo([...nomsFin].sort((a, b) => a.localeCompare(b, 'fr')));
      setDossiersFinanceurs(dfRes.data || []);
      setFinanceursParAf(Object.fromEntries(Object.entries((dfRes.data || []).reduce((acc, df) => { if (df.financeur_nom) (acc[df.action_formation_id] ||= new Set()).add(df.financeur_nom); return acc; }, {})).map(([k, v]) => [k, [...v].sort().join(', ')])));
      setFormateurs(formateursRes.data);
      setModules(modulesRes.data);
      setAffectationsHP(hpRes.data);
    } catch (error) {
      // erreur silencieuse
    } finally {
      setLoading(false);
    }
  };

  // Color map: formateur_id -> color object
  const formateurColorMap = useMemo(() => {
    const map = {};
    formateurs.forEach((f, idx) => {
      map[f.id] = FORMATEUR_COLORS[idx % FORMATEUR_COLORS.length];
    });
    return map;
  }, [formateurs]);

  const getFormateurColor = (formateurId) => {
    return formateurColorMap[formateurId] || FORMATEUR_COLORS[0];
  };

  const getFormateurInitials = (formateurId) => {
    const formateur = formateurs.find(f => f.id === formateurId);
    if (!formateur) return '??';
    return `${formateur.prenom?.[0] || ''}${formateur.nom?.[0] || ''}`.toUpperCase();
  };

  const getFormateurName = (formateurId) => {
    const formateur = formateurs.find(f => f.id === formateurId);
    return formateur ? `${formateur.nom} ${formateur.prenom}` : 'Non assigné';
  };

  const getModuleName = (moduleId) => {
    if (!moduleId) return null;
    const module = modules.find(m => m.id === moduleId);
    return module?.titre || null;
  };

  // Get display module name for a planning entry (check modules_pedagogiques first)
  const getPlanningModuleLabel = (p) => {
    // Try main module_id first
    if (p.module_id) {
      const name = getModuleName(p.module_id);
      if (name) return name + ' (à confirmer)';
    }
    // Try modules_pedagogiques
    const mp = (p.modules_pedagogiques || [])[0];
    if (mp?.module_id) {
      const name = getModuleName(mp.module_id);
      if (name) return name + ' (à confirmer)';
    }
    return 'Module inconnu';
  };

  const getModuleCode = (moduleId) => {
    const module = modules.find(m => m.id === moduleId);
    return module?.code || '';
  };

  // Calendar view helpers
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const getEventsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const events = [];
    dossiers.forEach(dossier => {
      const planningEntries = dossier.planning?.filter(p => p.date === dateStr) || [];
      planningEntries.forEach(entry => {
        events.push({ ...entry, dossier });
      });
    });
    return events;
  };

  // Gantt view helpers
  const monthStart = useMemo(() => startOfMonth(currentDate), [currentDate]);
  const monthEnd = useMemo(() => endOfMonth(currentDate), [currentDate]);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getDossierRange = (dossier) => {
    const start = parseISO(dossier.date_debut);
    const end = dossier.date_fin ? parseISO(dossier.date_fin) : start;
    return { start, end };
  };

  const isDossierOnDay = (dossier, day) => {
    const { start, end } = getDossierRange(dossier);
    return isWithinInterval(day, { start, end });
  };

  const getPlanningForDay = (dossier, day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return dossier.planning?.filter(p => p.date === dateStr) || [];
  };

  // Financeur d'une action (DF de l'AF, sinon de l'AF mère pour une AFT, sinon champ financeur)
  const financeurDe = (d) => financeursParAf[d.id] || (d.af_mere_id && financeursParAf[d.af_mere_id]) || d.financeur || '';
  const groupeDe = (d) => `${financeurDe(d) || 'Sans financeur'} — ${d.lieu_principal || 'Lieu non défini'}`;
  // Dossiers financeurs par AF -> financeurs / entreprises finales (AFT : repli sur l'AF mere) — memes regles que la liste des Actions de formation
  const dfParAf = useMemo(() => {
    const map = {};
    dossiersFinanceurs.forEach(df => {
      const e = (map[df.action_formation_id] ||= { financeurs: [], entreprises: [] });
      if (df.financeur_nom && !e.financeurs.includes(df.financeur_nom)) e.financeurs.push(df.financeur_nom);
      const ent = (df.entreprise_finale || '').trim();
      if (ent && !e.entreprises.includes(ent)) e.entreprises.push(ent);
    });
    return map;
  }, [dossiersFinanceurs]);
  const financeursDeDossier = (d) => (dfParAf[d.id]?.financeurs?.length ? dfParAf[d.id].financeurs : (d.type_dossier === 'aft' ? (d.financeur ? [d.financeur] : (dfParAf[d.af_mere_id]?.financeurs || [])) : (d.financeur ? [d.financeur] : [])));
  const entreprisesDeDossier = (d) => (dfParAf[d.id]?.entreprises?.length ? dfParAf[d.id].entreprises : (d.type_dossier === 'aft' ? (dfParAf[d.af_mere_id]?.entreprises || []) : []));
  const dossiersDuMois = useMemo(() => dossiers.filter(dossier => {
    const { start, end } = getDossierRange(dossier);
    return isWithinInterval(monthStart, { start, end }) ||
           isWithinInterval(monthEnd, { start, end }) ||
           (start <= monthStart && end >= monthEnd) ||
           (start >= monthStart && start <= monthEnd) ||
           (end >= monthStart && end <= monthEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [dossiers, monthStart, monthEnd]);
  const distinctMois = (fn) => [...new Set(dossiersDuMois.flatMap(fn).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));
  const ganttFinanceurs = distinctMois(financeursDeDossier);
  const ganttEntreprises = distinctMois(entreprisesDeDossier);
  const ganttLieux = distinctMois(d => [d.lieu_principal]);
  const nbGanttFiltres = (ganttFilters.type !== 'tous') + (ganttFilters.financeur !== 'all') + (ganttFilters.entreprise !== 'all') + (ganttFilters.lieu !== 'all');
  const filteredDossiers = useMemo(() => {
    const cle = (d) => { const f = financeurDe(d), l = d.lieu_principal || ''; return [f ? 0 : 1, f.toLowerCase(), l ? 0 : 1, l.toLowerCase(), d.date_debut || '']; };
    return dossiersDuMois.filter(d =>
      (ganttFilters.type === 'tous' || (d.type_dossier || 'af') === ganttFilters.type) &&
      (ganttFilters.financeur === 'all' || financeursDeDossier(d).includes(ganttFilters.financeur)) &&
      (ganttFilters.entreprise === 'all' || entreprisesDeDossier(d).includes(ganttFilters.entreprise)) &&
      (ganttFilters.lieu === 'all' || (d.lieu_principal || '') === ganttFilters.lieu)
    ).sort((a, b) => { const ka = cle(a), kb = cle(b); for (let i = 0; i < ka.length; i++) { if (ka[i] < kb[i]) return -1; if (ka[i] > kb[i]) return 1; } return 0; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossiersDuMois, financeursParAf, dfParAf, ganttFilters]);

  // Autres activites (conges, dev. projet, teletravail...) des collaborateurs sur le mois -> lignes en bas du Gantt
  const autresActivitesMois = useMemo(() => {
    const ms = format(monthStart, 'yyyy-MM-dd'), me = format(monthEnd, 'yyyy-MM-dd');
    const parFormateur = {};
    affectationsHP.forEach(hp => { if (hp.formateur_id && hp.date >= ms && hp.date <= me) (parFormateur[hp.formateur_id] ||= {})[hp.date] = hp; });
    return Object.entries(parFormateur).map(([fid, jours]) => ({ formateur: formateurs.find(f => f.id === fid), jours })).filter(x => x.formateur)
      .sort((a, b) => `${a.formateur.nom} ${a.formateur.prenom}`.localeCompare(`${b.formateur.nom} ${b.formateur.prenom}`, 'fr'));
  }, [affectationsHP, formateurs, monthStart, monthEnd]);

  // Build conflict/mutualization map
  // Key: "formateurId-date" -> [{dossier_id, module_id, dossier_intitule}]
  const formateurDayMap = useMemo(() => {
    const map = {};
    dossiers.forEach(dossier => {
      (dossier.planning || []).forEach(p => {
        const ids = [p.formateur_id, ...(p.intervenants || []).map(i => i.formateur_id)].filter(Boolean);
        ids.forEach(fid => {
          const key = `${fid}-${p.date}`;
          if (!map[key]) map[key] = [];
          map[key].push({
            dossier_id: dossier.id,
            dossier_intitule: dossier.intitule,
            module_id: p.module_id,
            demi_journee: fid === p.formateur_id ? (p.formateur_creneau || p.demi_journee) : ((p.intervenants || []).find(i => i.formateur_id === fid)?.creneau || 'journee')
          });
        });
      });
    });
    return map;
  }, [dossiers]);

  // Key: "formateurId-date" -> affectation hors production (congé, maladie, HP...)
  const hpDayMap = useMemo(() => {
    const map = {};
    affectationsHP.forEach(hp => { if (hp.formateur_id && hp.date) map[`${hp.formateur_id}-${hp.date}`] = hp; });
    return map;
  }, [affectationsHP]);

  // Conflict when dragging a formateur onto a cell: 'hp' (blocking) or 'af' (confirmation)
  const getDragConflict = (formateurId, dateStr, dossierId) => {
    const hp = hpDayMap[`${formateurId}-${dateStr}`];
    if (hp) return { type: 'hp', label: HP_LABELS[hp.type_affectation] || hp.type_affectation };
    const others = (formateurDayMap[`${formateurId}-${dateStr}`] || []).filter(e => e.dossier_id !== dossierId);
    if (others.length > 0) {
      const codes = [...new Set(others.map(e => dossiers.find(d => d.id === e.dossier_id)?.code || e.dossier_intitule))];
      return { type: 'af', codes };
    }
    return null;
  };

  // For a given cell (formateur on a date for a dossier), determine status
  const getCellStatus = (formateurId, date, dossierId, moduleId) => {
    if (!formateurId) return null;
    const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
    const key = `${formateurId}-${dateStr}`;
    const entries = formateurDayMap[key] || [];
    const otherEntries = entries.filter(e => e.dossier_id !== dossierId);
    
    if (otherEntries.length === 0) return null; // No overlap

    const allSameModule = otherEntries.every(e => e.module_id === moduleId);
    if (allSameModule && moduleId) {
      return { type: 'mutualized', count: entries.length };
    }
    return { type: 'conflict', count: entries.length, entries };
  };

  // Drag & Drop handlers
  const handleDragStart = (e, formateur) => {
    setDraggedFormateur(formateur);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', formateur.id);
  };

  const handleDragOver = (e, dossierId, dateStr) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropTarget(`${dossierId}-${dateStr}`);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const applyAffectation = async (dossier, dateStr, formateur) => {
    const updatedPlanning = dossier.planning.map(p => 
      p.date === dateStr ? { ...p, formateur_id: formateur.id } : p
    );
    try {
      const res = await axios.patch(`${API_URL}/dossiers/${dossier.id}/planning`, { planning: updatedPlanning });
      setDossiers(prev => prev.map(d => d.id === dossier.id ? res.data : d));
      toast.success(`${formateur.nom} ${formateur.prenom} affecté`);
    } catch (error) {
      toast.error('Erreur lors de l\'affectation');
    }
  };

  const handleDrop = async (e, dossier, dateStr) => {
    e.preventDefault();
    setDropTarget(null);
    
    if (!draggedFormateur) return;
    const formateur = draggedFormateur;
    setDraggedFormateur(null);

    const existingEntry = dossier.planning?.find(p => p.date === dateStr);
    if (!existingEntry) {
      toast.error('Cette date n\'est pas dans le planning du dossier');
      return;
    }

    const conflict = getDragConflict(formateur.id, dateStr, dossier.id);
    if (conflict?.type === 'hp') {
      toast.error(`${formateur.nom} ${formateur.prenom} est en « ${conflict.label} » le ${format(parseISO(dateStr), 'dd/MM/yyyy')} — affectation impossible`);
      return;
    }
    if (conflict?.type === 'af') {
      setConfirmDrop({ dossier, dateStr, formateur, conflict });
      return;
    }
    await applyAffectation(dossier, dateStr, formateur);
  };

  const confirmConflictDrop = async () => {
    const { dossier, dateStr, formateur } = confirmDrop;
    setConfirmDrop(null);
    await applyAffectation(dossier, dateStr, formateur);
  };

  // Drag an assigned formateur pastille out of the planning to remove it
  const handleAssignedDragStart = (e, dossier, dateStr, formateurId) => {
    e.stopPropagation();
    setDraggedAssigned({ dossier, dateStr, formateurId });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `remove:${dossier.id}:${dateStr}`);
  };

  const handleRemoveDrop = async (e) => {
    e.preventDefault();
    setRemoveHover(false);
    if (!draggedAssigned) return;
    const { dossier, dateStr, formateurId } = draggedAssigned;
    const updatedPlanning = dossier.planning.map(p =>
      p.date === dateStr && p.formateur_id === formateurId ? { ...p, formateur_id: '' } : p
    );
    try {
      const res = await axios.patch(`${API_URL}/dossiers/${dossier.id}/planning`, { planning: updatedPlanning });
      setDossiers(prev => prev.map(d => d.id === dossier.id ? res.data : d));
      toast.success(`${getFormateurName(formateurId)} retiré du ${format(parseISO(dateStr), 'dd/MM/yyyy')}`);
    } catch (error) {
      toast.error('Erreur lors du retrait');
    }
    setDraggedAssigned(null);
  };

  // Formateurs with an active contract overlapping the displayed month
  const isFormateurActifSurPeriode = (f) => {
    const debut = format(monthStart, 'yyyy-MM-dd');
    const fin = format(monthEnd, 'yyyy-MM-dd');
    return (f.contrats || []).some(c =>
      c.actif !== false &&
      (!c.date_debut || c.date_debut <= fin) &&
      (!c.date_fin || c.date_fin >= debut)
    );
  };
  const formateursExternes = useMemo(() => formateurs.filter(f => f.statut_intervenant === 'externe'), [formateurs]);
  const formateursActifs = useMemo(() => formateurs.filter(f => f.statut_intervenant !== 'externe' && isFormateurActifSurPeriode(f)), [formateurs, monthStart, monthEnd]); // eslint-disable-line react-hooks/exhaustive-deps
  const formateursInactifs = useMemo(() => formateurs.filter(f => f.statut_intervenant !== 'externe' && !isFormateurActifSurPeriode(f)), [formateurs, monthStart, monthEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderFormateurChip = (formateur) => {
    const color = getFormateurColor(formateur.id);
    return (
      <div
        key={formateur.id}
        draggable
        onDragStart={(e) => handleDragStart(e, formateur)}
        onDragEnd={() => setDraggedFormateur(null)}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md cursor-grab active:cursor-grabbing border ${color.light} ${color.border} select-none transition-shadow hover:shadow-md`}
        data-testid={`drag-formateur-${formateur.id}`}
      >
        <GripVertical className="w-3 h-3 text-slate-400" />
        <div className={`w-5 h-5 rounded-full ${color.bg} text-white text-[9px] flex items-center justify-center font-semibold`}>
          {getFormateurInitials(formateur.id)}
        </div>
        <span className={`text-xs font-medium ${color.text}`}>
          {formateur.nom} {formateur.prenom}
        </span>
      </div>
    );
  };

  // Update formateur for a cell via select dropdown
  const handleFormateurChange = async (dossier, dateStr, newFormateurId) => {
    const updatedPlanning = dossier.planning.map(p =>
      p.date === dateStr
        ? { ...p, formateur_id: newFormateurId === 'none' ? '' : newFormateurId }
        : p
    );
    try {
      const res = await axios.patch(`${API_URL}/dossiers/${dossier.id}/planning`, {
        planning: updatedPlanning
      });
      setDossiers(prev => prev.map(d => d.id === dossier.id ? res.data : d));
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Unassigned AF days for the current month (days in planning with no formateur)
  const unassignedDays = useMemo(() => {
    const result = [];
    dossiers.forEach(dossier => {
      (dossier.planning || []).forEach(p => {
        if (!p.formateur_id && p.date) {
          const pDate = parseISO(p.date);
          if (isWithinInterval(pDate, { start: monthStart, end: monthEnd })) {
            result.push({
              dossier_id: dossier.id,
              dossier_code: dossier.code,
              dossier_intitule: dossier.intitule,
              date: p.date,
              module_id: p.module_id,
              type_journee: p.type_journee,
            });
          }
        }
      });
    });
    return result;
  }, [dossiers, monthStart, monthEnd]);

  // Build formateur activity map for current month
  const formateurActivityMap = useMemo(() => {
    const map = {};
    formateurs.forEach(f => { map[f.id] = {}; });
    dossiers.forEach(dossier => {
      (dossier.planning || []).forEach(p => {
        if (!p.date) return;
        const ids = [p.formateur_id, ...(p.intervenants || []).map(i => i.formateur_id)].filter(Boolean);
        ids.forEach(fid => {
          if (!map[fid]) map[fid] = {};
          if (!map[fid][p.date]) map[fid][p.date] = [];
          map[fid][p.date].push({
            type: 'af', dossier_id: dossier.id, dossier_code: dossier.code,
            dossier_intitule: dossier.intitule, module_id: p.module_id,
            intervenant: fid !== p.formateur_id,
            creneau: fid === p.formateur_id ? (p.formateur_creneau || 'journee') : ((p.intervenants || []).find(i => i.formateur_id === fid)?.creneau || 'journee'),
          });
        });
      });
    });
    affectationsHP.forEach(hp => {
      const fid = hp.formateur_id;
      if (!map[fid]) map[fid] = {};
      if (!map[fid][hp.date]) map[fid][hp.date] = [];
      map[fid][hp.date].push({
        ...hp, type: hp.type_affectation || 'hors_production', note: hp.note || '', id: hp.id,
      });
    });
    return map;
  }, [dossiers, formateurs, affectationsHP]);

  const saveHpDialog = async (payload) => {
    setHpSaving(true);
    try {
      if (payload.id) {
        await axios.put(`${API_URL}/requetes/affectations-hors-production/${payload.id}`, { objectif: payload.objectif });
        toast.success('Objectif enregistré');
      } else {
        await axios.post(`${API_URL}/requetes/affectations-hors-production`, {
          formateur_id: payload.formateur_id, date: payload.date, type_affectation: payload.type_affectation, objectif: payload.objectif,
        });
        const f = formateurs.find(fo => fo.id === payload.formateur_id);
        toast.success(`Dév. pédagogique affecté à ${f?.nom || ''} ${f?.prenom || ''} le ${payload.date}`);
      }
      setHpDialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setHpSaving(false);
    }
  };

  const handleActivityDrop = async (e, formateurId, dateStr) => {
    e.preventDefault();
    setDropTarget(null);
    if (draggedActivity) {
      if (draggedActivity.value === 'developpement_pedagogique') {
        setHpDialog({ formateur_id: formateurId, date: dateStr, type_affectation: draggedActivity.value });
        setDraggedActivity(null);
        return;
      }
      try {
        await axios.post(`${API_URL}/requetes/affectations-hors-production`, {
          formateur_id: formateurId, date: dateStr, type_affectation: draggedActivity.value,
        });
        fetchData();
        const f = formateurs.find(fo => fo.id === formateurId);
        toast.success(`${draggedActivity.short} affecté à ${f?.nom || ''} ${f?.prenom || ''} le ${dateStr}`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur');
      }
      setDraggedActivity(null);
      return;
    }
    if (draggedUnassigned) {
      const dossier = dossiers.find(d => d.id === draggedUnassigned.dossier_id);
      if (!dossier) { setDraggedUnassigned(null); return; }
      if (draggedUnassigned.date !== dateStr) {
        toast.error('Glissez cette tuile AF sur la date correspondante (' + draggedUnassigned.date + ')');
        setDraggedUnassigned(null);
        return;
      }
      const updatedPlanning = dossier.planning.map(p =>
        p.date === dateStr && !p.formateur_id ? { ...p, formateur_id: formateurId } : p
      );
      try {
        const res = await axios.patch(`${API_URL}/dossiers/${dossier.id}/planning`, { planning: updatedPlanning });
        setDossiers(prev => prev.map(d => d.id === dossier.id ? res.data : d));
        const f = formateurs.find(fo => fo.id === formateurId);
        toast.success(`${dossier.code} affecté à ${f?.nom || ''} ${f?.prenom || ''}`);
      } catch (error) {
        toast.error("Erreur lors de l'affectation");
      }
      setDraggedUnassigned(null);
    }
  };

  const handleRemoveHP = async (hpId) => {
    try {
      await axios.delete(`${API_URL}/requetes/affectations-hors-production/${hpId}`);
      setHpDialog(null);
      fetchData();
      toast.success('Affectation supprimée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="planning-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Planning</h1>
            <p className="text-slate-500 mt-1">Visualisez et gérez les affectations des collaborateurs</p>
          </div>
          <Button variant="outline" onClick={() => setPdfDialogOpen(true)} data-testid="btn-export-pdf">
            <FileDown className="w-4 h-4 mr-2" /> Exporter PDF
          </Button>
        </div>

        <Tabs value={viewMode} onValueChange={setViewMode} className="space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
            <TabsList className="bg-slate-100 w-max">
              <TabsTrigger value="calendar" className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                Calendrier
              </TabsTrigger>
              <TabsTrigger value="gantt" className="flex items-center gap-2">
                <LayoutGrid className="w-4 h-4" />
                Affectation des formateurs
              </TabsTrigger>
              <TabsTrigger value="activites" className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                Affectation des activités
              </TabsTrigger>
            </TabsList>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="icon" 
                onClick={() => setCurrentDate(viewMode === 'calendar' ? subWeeks(currentDate, 1) : subMonths(currentDate, 1))}
                data-testid="planning-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium min-w-[200px] text-center capitalize">
                {viewMode === 'calendar' 
                  ? `${format(weekStart, 'd MMMM', { locale: fr })} - ${format(weekEnd, 'd MMMM yyyy', { locale: fr })}`
                  : format(currentDate, 'MMMM yyyy', { locale: fr })
                }
              </span>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={() => setCurrentDate(viewMode === 'calendar' ? addWeeks(currentDate, 1) : addMonths(currentDate, 1))}
                data-testid="planning-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Calendar View */}
          <TabsContent value="calendar">
            <Card className="border-0 shadow-sm overflow-hidden">
              <CardContent className="p-0">
                <div className="grid grid-cols-7 bg-slate-50 border-b">
                  {weekDays.map((day) => (
                    <div key={day.toISOString()} className={`p-4 text-center border-r last:border-r-0 ${isJourFerieDate(day) ? 'bg-rose-50' : ''}`} title={getJourFerieLabel(format(day, 'yyyy-MM-dd')) || undefined}>
                      <p className={`text-xs font-medium uppercase ${isJourFerieDate(day) ? 'text-rose-600' : 'text-slate-500'}`}>
                        {format(day, 'EEEE', { locale: fr })}
                      </p>
                      <p className={`text-2xl font-bold mt-1 ${isJourFerieDate(day) ? 'text-rose-700' : 'text-slate-900'}`}>
                        {format(day, 'd')}
                      </p>
                      {isJourFerieDate(day) && <p className="text-[10px] text-rose-500 truncate" data-testid={`ferie-header-${format(day, 'yyyy-MM-dd')}`}>{getJourFerieLabel(format(day, 'yyyy-MM-dd'))}</p>}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 min-h-[400px]">
                  {weekDays.map((day) => {
                    const events = getEventsForDay(day);
                    return (
                      <div key={day.toISOString()} className="p-2 border-r last:border-r-0 border-b min-h-[150px]">
                        {events.length === 0 ? (
                          <p className="text-xs text-slate-400 text-center mt-4">Aucune séance</p>
                        ) : (
                          <div className="space-y-2">
                            {events.map((event) => {
                              const color = getFormateurColor(event.formateur_id);
                              const status = getCellStatus(event.formateur_id, day, event.dossier.id, event.module_id);
                              return (
                                <div 
                                  key={`${event.dossier.id}-${event.formateur_id}`}
                                  className={`p-2 rounded-lg border text-xs cursor-pointer hover:shadow-md transition-shadow ${color.light} ${color.border} ${
                                    status?.type === 'conflict' ? 'ring-2 ring-amber-300 ring-offset-1' : ''
                                  }`}
                                  onClick={() => navigate(`/dossiers/${event.dossier.id}/journee/${format(day, 'yyyy-MM-dd')}`)}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span className={`font-semibold ${color.text}`}>
                                      {getFormateurInitials(event.formateur_id)}
                                    </span>
                                    <div className="flex items-center gap-1">
                                      {status?.type === 'mutualized' && (
                                        <span className="flex items-center gap-0.5 text-[10px] text-blue-600 bg-blue-50 px-1 rounded" title="Mutualisé">
                                          <Link2 className="w-2.5 h-2.5" />x{status.count}
                                        </span>
                                      )}
                                      {status?.type === 'conflict' && (
                                        <span className="flex items-center gap-0.5 text-[10px] text-amber-600 bg-amber-50 px-1 rounded" title="Aussi sur d'autres dossiers">
                                          <AlertTriangle className="w-2.5 h-2.5" />x{status.count}
                                        </span>
                                      )}
                                      <span className="text-slate-500 capitalize">
                                        {event.demi_journee === 'journee' ? 'J' : event.demi_journee === 'matin' ? 'M' : 'AM'}
                                      </span>
                                    </div>
                                  </div>
                                  <p className="text-slate-700 truncate font-medium">{event.dossier.intitule}</p>
                                  {event.type_journee === 'stage' && event.organisme_externe ? (
                                    <p className="text-violet-600 font-semibold truncate">{event.organisme_externe} (Stage)</p>
                                  ) : (
                                    <p className="text-slate-500 truncate">{getModuleName(event.module_id) || getModuleName((event.modules_pedagogiques || [])[0]?.module_id) || 'Module inconnu'}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gantt View */}
          <TabsContent value="gantt">
            {/* Draggable formateurs panel */}
            <Card className="border-0 shadow-sm mb-4">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[300px]">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
                      Glissez un formateur sur le planning pour l'affecter — formateurs actifs en {format(currentDate, 'MMMM yyyy', { locale: fr })}
                    </p>
                    <div className="flex flex-wrap gap-2" data-testid="formateurs-actifs-list">
                      {formateursActifs.map(renderFormateurChip)}
                      {formateursActifs.length === 0 && (
                        <p className="text-sm text-slate-400">Aucun formateur avec un contrat actif sur cette période</p>
                      )}
                      {formateursInactifs.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-dashed border-slate-300 text-sm text-slate-500 hover:border-slate-400 hover:text-slate-700 transition-colors"
                              data-testid="formateurs-inactifs-toggle"
                            >
                              <ChevronDown className="w-4 h-4" />
                              {formateursInactifs.length} sans contrat sur la période
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-80 p-3" data-testid="formateurs-inactifs-list">
                            <p className="text-[11px] text-slate-500 mb-2">Formateurs sans contrat actif sur {format(currentDate, 'MMMM yyyy', { locale: fr })}. Glissez pour affecter malgré tout.</p>
                            <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
                              {formateursInactifs.map(renderFormateurChip)}
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                      {formateursExternes.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-dashed border-violet-300 text-sm text-violet-600 hover:border-violet-400 hover:text-violet-800 hover:bg-violet-50 transition-colors"
                              data-testid="formateurs-externes-toggle"
                            >
                              <ChevronDown className="w-4 h-4" />
                              {formateursExternes.length} formateur{formateursExternes.length > 1 ? 's' : ''} extérieur{formateursExternes.length > 1 ? 's' : ''}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-80 p-3" data-testid="formateurs-externes-list">
                            <p className="text-[11px] text-slate-500 mb-2">Intervenants extérieurs (sans contrat, non payés, non facturés). Glissez pour les affecter sur une journée.</p>
                            <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
                              {formateursExternes.map(renderFormateurChip)}
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </div>
                  <div
                    className={`flex flex-col items-center justify-center gap-1 px-4 py-3 rounded-lg border-2 border-dashed min-w-[180px] min-h-[64px] transition-colors ${
                      draggedAssigned
                        ? removeHover ? 'border-red-500 bg-red-100 text-red-700' : 'border-red-400 bg-red-50 text-red-600 animate-pulse'
                        : 'border-slate-200 text-slate-400'
                    }`}
                    onDragOver={(e) => { if (draggedAssigned) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setRemoveHover(true); } }}
                    onDragLeave={() => setRemoveHover(false)}
                    onDrop={handleRemoveDrop}
                    data-testid="remove-formateur-dropzone"
                  >
                    <Trash2 className="w-5 h-5" />
                    <span className="text-xs font-medium text-center">Glissez une pastille ici pour retirer le formateur</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Filtres (memes criteres que la liste des Actions de formation) */}
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="gantt-filters">
              <div className="flex rounded-lg border bg-white p-0.5" data-testid="gantt-filter-type">
                {[['tous', 'AF + AFT'], ['af', 'AF'], ['aft', 'AFT']].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setGanttFilters({ ...ganttFilters, type: k })} data-testid={`gantt-type-${k}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${ganttFilters.type === k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{l}</button>
                ))}
              </div>
              <Select value={ganttFilters.financeur} onValueChange={(v) => setGanttFilters({ ...ganttFilters, financeur: v })}>
                <SelectTrigger className="h-9 text-xs w-[210px]" data-testid="gantt-filter-financeur"><SelectValue placeholder="Financeur" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tous les financeurs</SelectItem>{ganttFinanceurs.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={ganttFilters.entreprise} onValueChange={(v) => setGanttFilters({ ...ganttFilters, entreprise: v })}>
                <SelectTrigger className="h-9 text-xs w-[220px]" data-testid="gantt-filter-entreprise"><SelectValue placeholder="Entreprise finale" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Toutes les entreprises finales</SelectItem>{ganttEntreprises.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={ganttFilters.lieu} onValueChange={(v) => setGanttFilters({ ...ganttFilters, lieu: v })}>
                <SelectTrigger className="h-9 text-xs w-[200px]" data-testid="gantt-filter-lieu"><SelectValue placeholder="Lieu" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tous les lieux</SelectItem>{ganttLieux.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
              {nbGanttFiltres > 0 && <button type="button" onClick={() => setGanttFilters({ type: 'tous', financeur: 'all', entreprise: 'all', lieu: 'all' })} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="gantt-reset-filters"><X className="w-3.5 h-3.5" /> Réinitialiser</button>}
              <span className="text-xs text-slate-400 ml-auto whitespace-nowrap" data-testid="gantt-count">{filteredDossiers.length}/{dossiersDuMois.length} action(s)</span>
            </div>

            {/* Gantt chart */}
            <Card className="border-0 shadow-sm overflow-hidden">
              <CardContent className="p-0 overflow-auto max-h-[calc(100vh-260px)]" data-testid="gantt-scroll">
                <div className="min-w-[1200px]">
                  {/* Header with dates */}
                  <div className="flex border-b bg-slate-50 sticky top-0 z-30">
                    <div className="w-56 flex-shrink-0 p-3 border-r font-medium text-slate-700 text-sm sticky left-0 z-40 bg-slate-50 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                      Dossiers
                    </div>
                    <div className="flex-1 flex">
                      {monthDays.map((day) => {
                        const dayOfWeek = day.getDay();
                        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                        const today = isToday(day);
                        return (
                          <div 
                            key={day.toISOString()} 
                            className={`flex-shrink-0 w-12 p-1 text-center border-r text-xs ${today ? 'bg-indigo-600 text-white' : isJourFerieDate(day) ? 'bg-rose-100' : isWeekend ? 'bg-slate-100' : ''}`}
                            title={getJourFerieLabel(format(day, 'yyyy-MM-dd')) || undefined}
                          >
                            <p className={`text-[10px] ${today ? 'text-indigo-200' : isJourFerieDate(day) ? 'text-rose-500' : 'text-slate-400'}`}>{format(day, 'EEE', { locale: fr })}</p>
                            <p className={`font-semibold ${today ? 'text-white' : isJourFerieDate(day) ? 'text-rose-700' : isWeekend ? 'text-slate-400' : 'text-slate-900'}`}>{format(day, 'd')}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Dossiers rows */}
                  {filteredDossiers.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      Aucun dossier pour cette période
                    </div>
                  ) : (
                    filteredDossiers.map((dossier, di) => (
                      <React.Fragment key={dossier.id}>
                      {(di === 0 || groupeDe(filteredDossiers[di - 1]) !== groupeDe(dossier)) && (
                        <div className="flex border-b bg-slate-200/70" data-testid={`gantt-groupe-${di}`}>
                          <div className="w-56 flex-shrink-0 px-2 py-1 border-r sticky left-0 z-20 bg-slate-200/70 text-[10px] font-bold uppercase tracking-wide text-slate-700 truncate" title={groupeDe(dossier)}>{groupeDe(dossier)}</div>
                          <div className="flex-1" />
                        </div>
                      )}
                      <div className="flex border-b" data-testid={`gantt-row-${dossier.id}`}>
                        <div
                          className="w-56 flex-shrink-0 px-2 py-1 border-r bg-white cursor-pointer hover:bg-indigo-50 transition-colors sticky left-0 z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] flex flex-col justify-center"
                          onClick={() => navigate(`/dossiers/${dossier.id}?tab=planning_pedagogique`)}
                          title={`${dossier.code} — ${dossier.intitule}\n${dossier.client || ''}\nOuvrir le planning pédagogique`}
                          data-testid={`gantt-dossier-link-${dossier.id}`}
                        >
                          <p className="text-xs font-medium text-indigo-700 truncate leading-tight hover:underline"><span className="font-mono text-[9px] text-slate-400 mr-1">{dossier.code}</span>{dossier.intitule}</p>
                          <p className="text-[10px] text-slate-500 truncate leading-tight">{dossier.client}</p>
                        </div>
                        <div className="flex-1 flex">
                          {monthDays.map((day, idx) => {
                            const dateStr = format(day, 'yyyy-MM-dd');
                            const isInRange = isDossierOnDay(dossier, day);
                            const dayPlanning = getPlanningForDay(dossier, day);
                            const dayOfWeek = day.getDay();
                            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                            const isDropping = dropTarget === `${dossier.id}-${dateStr}`;
                            const hasPlanning = dayPlanning.length > 0;
                            const today = isToday(day);
                            const dragConflict = draggedFormateur && isInRange && hasPlanning ? getDragConflict(draggedFormateur.id, dateStr, dossier.id) : null;
                            
                            return (
                              <div 
                                key={day.toISOString()} 
                                data-testid={`gantt-cell-${dossier.id}-${dateStr}`}
                                data-conflict={dragConflict?.type || undefined}
                                title={dragConflict?.type === 'hp' ? `${draggedFormateur.nom} ${draggedFormateur.prenom} : ${dragConflict.label} — affectation impossible` : dragConflict?.type === 'af' ? `${draggedFormateur.nom} ${draggedFormateur.prenom} déjà affecté sur ${dragConflict.codes.join(', ')}` : undefined}
                                className={`flex-shrink-0 w-12 border-r relative flex flex-col items-center justify-center gap-0.5 transition-colors ${
                                  isDropping && draggedFormateur ? (dragConflict?.type === 'hp' ? 'bg-red-300 ring-2 ring-inset ring-red-500' : dragConflict?.type === 'af' ? 'bg-amber-200 ring-2 ring-inset ring-amber-500' : 'bg-indigo-100 ring-2 ring-inset ring-indigo-400') :
                                  dragConflict?.type === 'hp' ? 'bg-red-200' :
                                  dragConflict?.type === 'af' ? 'bg-amber-100' :
                                  today ? 'bg-indigo-50 border-l-2 border-l-indigo-400' :
                                  isJourFerieDate(day) ? 'bg-rose-50/70' :
                                  isInRange ? 'bg-indigo-50/50' : 
                                  isWeekend ? 'bg-slate-50' : ''
                                }`}
                                style={{ minHeight: '34px' }}
                                onDragOver={(e) => isInRange && hasPlanning ? handleDragOver(e, dossier.id, dateStr) : null}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => isInRange && hasPlanning ? handleDrop(e, dossier, dateStr) : null}
                              >
                                {dayPlanning.map((p, pIdx) => {
                                  const color = p.formateur_id ? getFormateurColor(p.formateur_id) : null;
                                  const status = getCellStatus(p.formateur_id, dateStr, dossier.id, p.module_id);
                                  const isStage = p.type_journee === 'stage';
                                  
                                  if (!p.formateur_id) {
                                    // Empty slot
                                    return (
                                      <div 
                                        key={`slot-${pIdx}`}
                                        className={`w-7 h-7 rounded border-2 border-dashed flex items-center justify-center cursor-pointer transition-colors ${
                                          isStage ? 'border-violet-300 bg-violet-50 hover:border-violet-500 hover:bg-violet-100' : 'border-slate-300 hover:border-indigo-400 hover:bg-indigo-50'
                                        }`}
                                        title={isStage ? `${p.organisme_externe || 'Stage'} - Cliquer pour voir` : 'Cliquer pour voir le détail / Glisser un formateur'}
                                        onClick={() => navigate(`/dossiers/${dossier.id}/journee/${dateStr}`)}
                                      >
                                        {isStage ? (
                                          <span className="text-[8px] font-bold text-violet-600">{p.organisme_externe || 'STG'}</span>
                                        ) : (
                                          <User className="w-3.5 h-3.5 text-slate-300" />
                                        )}
                                      </div>
                                    );
                                  }
                                  
                                  // Check validation status
                                  const isValidated = p.presences_validees === true && p.modules_confirmes === true;
                                  const hasStarted = p.has_cahier === true;
                                  const moduleLabel = p.modules_confirmes ? (getModuleName(p.module_id) || getModuleName((p.modules_pedagogiques || [])[0]?.module_id) || 'Module') : getPlanningModuleLabel(p);

                                  return (
                                    <div 
                                      key={`${p.formateur_id}-${p.module_id || ''}-${pIdx}`}
                                      draggable
                                      onDragStart={(e) => handleAssignedDragStart(e, dossier, dateStr, p.formateur_id)}
                                      onDragEnd={() => { setDraggedAssigned(null); setRemoveHover(false); }}
                                      data-testid={`gantt-pastille-${dossier.id}-${dateStr}`}
                                      className={`w-7 h-7 rounded ${color.bg} text-white text-[10px] flex items-center justify-center font-semibold relative cursor-grab active:cursor-grabbing hover:opacity-90 transition-opacity ${
                                        status?.type === 'conflict' ? 'ring-2 ring-amber-400 ring-offset-1' : ''
                                      }`}
                                      title={`${getFormateurName(p.formateur_id)} - ${moduleLabel}${
                                        isValidated ? ' ✓ Validé' : hasStarted ? ' ○ En cours' : ''
                                      } - Cliquer pour le détail / Glisser vers la corbeille pour retirer${
                                        status?.type === 'conflict' ? ' ⚠ Aussi affecté sur d\'autres modules' :
                                        status?.type === 'mutualized' ? ` ↔ Mutualisé (x${status.count})` : ''
                                      }`}
                                      onClick={() => navigate(`/dossiers/${dossier.id}/journee/${dateStr}`)}
                                    >
                                      {getFormateurInitials(p.formateur_id)}
                                      
                                      {/* Validation status badge */}
                                      {isValidated ? (
                                        <span className="absolute -bottom-1 -right-1 bg-emerald-500 text-white w-3 h-3 rounded-full flex items-center justify-center border border-white">
                                          <svg className="w-1.5 h-1.5" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </span>
                                      ) : p.formateur_id && (
                                        <span className="absolute -bottom-1 -right-1 bg-red-500 w-2.5 h-2.5 rounded-full border border-white" />
                                      )}
                                      
                                      {/* Mutualization badge */}
                                      {status?.type === 'mutualized' && (
                                        <span className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center border border-white">
                                          {status.count}
                                        </span>
                                      )}
                                      
                                      {/* Conflict badge - now amber (warning, not blocking) */}
                                      {status?.type === 'conflict' && (
                                        <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center border border-white">
                                          {status.count}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      </React.Fragment>
                    ))
                  )}

                  {/* Autres activites des collaborateurs sur le mois (conges, dev. projet, teletravail...) */}
                  {autresActivitesMois.length > 0 && (
                    <>
                      <div className="flex border-b border-t-2 border-slate-300 bg-amber-50" data-testid="gantt-autres-activites-header">
                        <div className="w-56 flex-shrink-0 px-2 py-1 border-r sticky left-0 z-20 bg-amber-50 text-[10px] font-bold uppercase tracking-wide text-amber-800 truncate">Autres activités des collaborateurs</div>
                        <div className="flex-1" />
                      </div>
                      {autresActivitesMois.map(({ formateur, jours }) => (
                        <div key={formateur.id} className="flex border-b" data-testid={`gantt-activite-row-${formateur.id}`}>
                          <div className="w-56 flex-shrink-0 px-2 py-1 border-r bg-white sticky left-0 z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] flex items-center gap-2 cursor-pointer hover:bg-indigo-50" onClick={() => setViewMode('activites')} title="Ouvrir l'affectation des activités">
                            <div className={`w-6 h-6 rounded-full ${getFormateurColor(formateur.id).bg} text-white text-[10px] flex items-center justify-center font-semibold flex-shrink-0`}>{getFormateurInitials(formateur.id)}</div>
                            <p className="text-xs font-medium text-slate-800 truncate">{formateur.nom} {formateur.prenom}</p>
                          </div>
                          <div className="flex-1 flex">
                            {monthDays.map((day) => {
                              const ds = format(day, 'yyyy-MM-dd');
                              const hp = jours[ds];
                              const act = hp ? (ACTIVITY_BY_VALUE[hp.type_affectation] || ACTIVITY_BY_VALUE.autre) : null;
                              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                              return (
                                <div key={ds} className={`flex-shrink-0 w-12 h-9 border-r flex items-center justify-center p-0.5 ${isWeekend ? 'bg-slate-50' : ''}`} data-testid={`gantt-activite-${formateur.id}-${ds}`}>
                                  {act && <span className={`w-full h-full rounded text-[9px] font-bold flex items-center justify-center ${act.light} ${act.text} border ${act.border}`} title={`${act.label}${hp.objectif ? ` — ${hp.objectif}` : ''}${hp.note ? ` — ${hp.note}` : ''}`}>{act.short}</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Legend */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <h3 className="font-medium text-slate-900 mb-3 text-sm">Collaborateurs actifs sur la période</h3>
                  <div className="flex flex-wrap gap-3">
                    {formateursActifs.map((formateur) => {
                      const color = getFormateurColor(formateur.id);
                      return (
                        <div key={formateur.id} className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded-md ${color.bg} text-white text-[10px] flex items-center justify-center font-semibold`}>
                            {getFormateurInitials(formateur.id)}
                          </div>
                          <span className="text-xs text-slate-600">
                            {formateur.nom} {formateur.prenom}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <h3 className="font-medium text-slate-900 mb-3 text-sm">Légende</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md border-2 border-dashed border-slate-300 flex items-center justify-center">
                        <User className="w-3 h-3 text-slate-300" />
                      </div>
                      <span className="text-xs text-slate-600">Séance sans collaborateur (glissez pour affecter)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <div className="w-6 h-6 rounded-md bg-blue-600"></div>
                        <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[7px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center border border-white">2</span>
                      </div>
                      <span className="text-xs text-slate-600">Mutualisé (même module, plusieurs dossiers)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <div className="w-6 h-6 rounded-md bg-emerald-600 ring-2 ring-amber-400 ring-offset-1"></div>
                        <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[7px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center border border-white">2</span>
                      </div>
                      <span className="text-xs text-slate-600">Multi-dossiers (modules différents le même jour)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md bg-amber-100 border border-amber-400"></div>
                      <span className="text-xs text-slate-600">Pendant le glisser : déjà affecté ailleurs ce jour (confirmation demandée)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md bg-red-200 border border-red-500"></div>
                      <span className="text-xs text-slate-600">Pendant le glisser : congé / maladie / hors production (affectation bloquée)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Dialog open={!!confirmDrop} onOpenChange={(o) => { if (!o) setConfirmDrop(null); }}>
              <DialogContent data-testid="conflict-confirm-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Formateur déjà affecté ce jour</DialogTitle>
                </DialogHeader>
                {confirmDrop && (
                  <p className="text-sm text-slate-600">
                    <span className="font-medium text-slate-900">{confirmDrop.formateur.nom} {confirmDrop.formateur.prenom}</span> est déjà affecté le{' '}
                    <span className="font-medium">{format(parseISO(confirmDrop.dateStr), 'dd/MM/yyyy')}</span> sur{' '}
                    <span className="font-medium">{confirmDrop.conflict.codes.join(', ')}</span>.
                    <br />Voulez-vous l'affecter quand même sur <span className="font-medium">{confirmDrop.dossier.code}</span> (mutualisation) ?
                  </p>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setConfirmDrop(null)} data-testid="conflict-cancel-btn">Annuler</Button>
                  <Button onClick={confirmConflictDrop} className="bg-amber-600 hover:bg-amber-700" data-testid="conflict-confirm-btn">Affecter quand même</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Affectation des activités View */}
          <TabsContent value="activites">
            {/* Draggable activity types + unassigned AF */}
            <Card className="border-0 shadow-sm mb-4">
              <CardContent className="p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
                  Glissez un type d'activité sur un formateur pour l'affecter
                  <span className="ml-2 normal-case font-normal text-slate-400" data-testid="activites-formateurs-info">— {formateursActifs.length} collaborateur(s) actif(s) en {format(currentDate, 'MMMM yyyy', { locale: fr })}{formateursInactifs.length > 0 ? `, ${formateursInactifs.length} sans contrat masqué(s)` : ''}</span>
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {ACTIVITY_TYPES.map((act) => (
                    <div
                      key={act.value}
                      draggable
                      onDragStart={(e) => { setDraggedActivity(act); setDraggedUnassigned(null); e.dataTransfer.effectAllowed = 'copy'; }}
                      onDragEnd={() => setDraggedActivity(null)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-grab active:cursor-grabbing border-2 ${act.light} ${act.border} select-none transition-shadow hover:shadow-md`}
                      data-testid={`drag-activity-${act.value}`}
                    >
                      <GripVertical className="w-3 h-3 text-slate-400" />
                      <span className={`text-xs font-bold ${act.bg} text-white px-1.5 py-0.5 rounded`}>{act.short}</span>
                      <span className={`text-sm font-medium ${act.text}`}>{act.label}</span>
                    </div>
                  ))}
                </div>

                {unassignedDays.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-2">
                      Journées AF non affectées ({unassignedDays.length})
                    </p>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {unassignedDays.map((ua) => (
                        <div
                          key={`${ua.dossier_id || ua.dossier_code}-${ua.date}`}
                          draggable
                          onDragStart={(e) => { setDraggedUnassigned(ua); setDraggedActivity(null); e.dataTransfer.effectAllowed = 'copy'; }}
                          onDragEnd={() => setDraggedUnassigned(null)}
                          className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg cursor-grab active:cursor-grabbing border-2 border-dashed border-amber-300 bg-amber-50 select-none hover:shadow-md transition-shadow"
                          title={`${ua.dossier_intitule} - ${ua.date}`}
                        >
                          <GripVertical className="w-3 h-3 text-amber-400" />
                          <span className="text-[10px] font-mono text-amber-700 bg-amber-100 px-1 rounded">{ua.dossier_code}</span>
                          <span className="text-xs text-amber-600">{format(parseISO(ua.date), 'dd/MM', { locale: fr })}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Activity Gantt by formateur */}
            <Card className="border-0 shadow-sm overflow-hidden">
              <CardContent className="p-0 overflow-auto max-h-[calc(100vh-260px)]" data-testid="activites-scroll">
                <div className="min-w-[1200px]">
                  {/* Header */}
                  <div className="flex border-b bg-slate-50 sticky top-0 z-30">
                    <div className="w-48 flex-shrink-0 p-3 border-r font-medium text-slate-700 text-sm sticky left-0 z-40 bg-slate-50 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                      Formateurs
                    </div>
                    <div className="flex-1 flex">
                      {monthDays.map((day) => {
                        const dayOfWeek = day.getDay();
                        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                        const today = isToday(day);
                        return (
                          <div key={day.toISOString()} className={`flex-shrink-0 w-12 p-1 text-center border-r text-xs ${today ? 'bg-indigo-600 text-white' : isJourFerieDate(day) ? 'bg-rose-100' : isWeekend ? 'bg-slate-100' : ''}`} title={getJourFerieLabel(format(day, 'yyyy-MM-dd')) || undefined}>
                            <p className={`text-[10px] ${today ? 'text-indigo-200' : isJourFerieDate(day) ? 'text-rose-500' : 'text-slate-400'}`}>{format(day, 'EEE', { locale: fr })}</p>
                            <p className={`font-semibold ${today ? 'text-white' : isJourFerieDate(day) ? 'text-rose-700' : isWeekend ? 'text-slate-400' : 'text-slate-900'}`}>{format(day, 'd')}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Formateur rows : uniquement les collaborateurs actifs sur le mois affiché */}
                  {formateursActifs.length === 0 ? (
                    <div className="p-8 text-center text-slate-500" data-testid="activites-aucun-formateur">Aucun formateur avec un contrat actif en {format(currentDate, 'MMMM yyyy', { locale: fr })}</div>
                  ) : (
                    formateursActifs.map((formateur) => {
                      const color = getFormateurColor(formateur.id);
                      const actMap = formateurActivityMap[formateur.id] || {};
                      return (
                        <div key={formateur.id} className="flex border-b" data-testid={`activites-row-${formateur.id}`}>
                          <div className="w-48 flex-shrink-0 p-2 border-r bg-white flex items-center gap-2 sticky left-0 z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                            <div className={`w-7 h-7 rounded-full ${color.bg} text-white text-xs flex items-center justify-center font-semibold flex-shrink-0`}>
                              {getFormateurInitials(formateur.id)}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 text-sm truncate">{formateur.nom} {formateur.prenom}</p>
                            </div>
                          </div>
                          <div className="flex-1 flex">
                            {monthDays.map((day, idx) => {
                              const dateStr = format(day, 'yyyy-MM-dd');
                              const dayOfWeek = day.getDay();
                              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                              const today = isToday(day);
                              const activities = actMap[dateStr] || [];
                              const isDropping = dropTarget === `act-${formateur.id}-${dateStr}`;

                              return (
                                <div
                                  key={day.toISOString()}
                                  className={`flex-shrink-0 w-12 border-r relative flex flex-col items-center justify-center gap-0.5 transition-colors ${
                                    isDropping ? 'bg-indigo-100 ring-2 ring-inset ring-indigo-400' :
                                    today ? 'bg-indigo-50 border-l-2 border-l-indigo-400' :
                                    isJourFerieDate(day) ? 'bg-rose-50/70' :
                                    isWeekend ? 'bg-slate-50' : ''
                                  }`}
                                  style={{ minHeight: '48px' }}
                                  onDragOver={(e) => { if (!isWeekend) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDropTarget(`act-${formateur.id}-${dateStr}`); } }}
                                  onDragLeave={() => setDropTarget(null)}
                                  onDrop={(e) => !isWeekend && handleActivityDrop(e, formateur.id, dateStr)}
                                >
                                  {activities.map((act, ai) => {
                                    if (act.type === 'af') {
                                      const dColor = getFormateurColor(formateur.id);
                                      return (
                                        <div
                                          key={act.id || `af-${act.dossier_id}-${ai}`}
                                          className={`w-10 h-5 rounded text-[8px] font-bold flex items-center justify-center cursor-pointer ${dColor.bg} text-white truncate ${act.intervenant ? 'opacity-70 ring-1 ring-inset ring-white/70' : ''}`}
                                          title={`${act.dossier_code} - ${act.dossier_intitule}${act.creneau && act.creneau !== 'journee' ? ` · ${act.creneau}` : ''}${act.intervenant ? ' (intervenant)' : ''}`}
                                          onClick={() => navigate(`/dossiers/${act.dossier_id}/planning`)}
                                        >
                                          {act.dossier_code?.replace('AF-2026-', '')}
                                        </div>
                                      );
                                    }
                                    const actType = ACTIVITY_BY_VALUE[act.type] || ACTIVITY_BY_VALUE.autre;
                                    const isDP = act.type === 'developpement_pedagogique';
                                    return (
                                      <div
                                        key={act.id || `hp-${act.type}-${ai}`}
                                        className={`w-10 h-5 rounded text-[8px] font-bold flex items-center justify-center cursor-pointer relative ${actType.bg} text-white ${isDP && !act.objectif ? 'ring-2 ring-amber-400' : ''}`}
                                        title={`${actType.label}${act.objectif ? ' — Objectif : ' + act.objectif : isDP ? ' — objectif à renseigner' : ''}${act.realise ? '\nRéalisé : ' + act.realise : ''}${act.note ? ' - ' + act.note : ''} (clic pour ouvrir)`}
                                        onContextMenu={(e) => { e.preventDefault(); if (act.id) handleRemoveHP(act.id); }}
                                        onClick={() => { if (act.id) setHpDialog(act); }}
                                        data-testid={`hp-tile-${act.id}`}
                                      >
                                        {actType.short}
                                        {act.realise && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 border border-white" title="Réalisé renseigné" />}
                                      </div>
                                    );
                                  })}
                                  {activities.length === 0 && !isWeekend && (
                                    <div className="w-10 h-5 rounded border border-dashed border-slate-200" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Legend */}
            <Card className="border-0 shadow-sm mt-4">
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-900 mb-3 text-sm">Légende</h3>
                <div className="flex flex-wrap gap-4">
                  {ACTIVITY_TYPES.map(act => (
                    <div key={act.value} className="flex items-center gap-2">
                      <div className={`w-10 h-5 rounded ${act.bg} text-white text-[9px] font-bold flex items-center justify-center`}>{act.short}</div>
                      <span className="text-xs text-slate-600">{act.label}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-5 rounded bg-blue-500 ring-2 ring-amber-400 text-white text-[9px] font-bold flex items-center justify-center">DP</div>
                    <span className="text-xs text-slate-600">Dév. pédagogique sans objectif (clic pour le renseigner)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-5 rounded bg-blue-500 text-white text-[9px] font-bold flex items-center justify-center relative">DP<span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 border border-white" /></div>
                    <span className="text-xs text-slate-600">Réalisé renseigné par le formateur</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-5 rounded bg-blue-600 text-white text-[9px] font-bold flex items-center justify-center">0001</div>
                    <span className="text-xs text-slate-600">Action de formation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-5 rounded border-2 border-dashed border-amber-300 bg-amber-50 text-[9px] font-bold flex items-center justify-center text-amber-600">AF</div>
                    <span className="text-xs text-slate-600">Journée AF non affectée (glisser sur un formateur)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>
      <ActiviteHpDialog open={!!hpDialog} onOpenChange={(o) => !o && setHpDialog(null)} hp={hpDialog}
        typeLabel={hpDialog ? (ACTIVITY_TYPES.find(a => a.value === hpDialog.type_affectation)?.label || HP_LABELS[hpDialog.type_affectation] || hpDialog.type_affectation) : ''}
        formateurNom={hpDialog ? `${formateurs.find(f => f.id === hpDialog.formateur_id)?.nom || ''} ${formateurs.find(f => f.id === hpDialog.formateur_id)?.prenom || ''}` : ''}
        onSave={saveHpDialog} onDelete={handleRemoveHP} saving={hpSaving} />
      <PlanningExportPdfDialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen} dossiers={dossiers} dossiersFinanceurs={dossiersFinanceurs} financeursDispo={financeursDispo} />
    </DashboardLayout>
  );
};
